// TypeScript types mirroring backend Pydantic schemas.
// Keep in sync with tdia.api.schemas and UI_SDD.md Section 3 / Section 8.

// ---------------------------------------------------------------------------
// Common
// ---------------------------------------------------------------------------

export type ScanState =
  | 'queued'
  | 'cloning'
  | 'planning'
  | 'running'
  | 'paused'
  | 'critic'
  | 'complete'
  | 'partial'
  | 'failed'

export type Severity = 'low' | 'medium' | 'high' | 'critical'

export type AgentStatus =
  | 'waiting'
  | 'running'
  | 'complete'
  | 'warned'   // tool not available in scanned repo — not a failure (SDD §14.12)
  | 'failed'
  | 'skipped'
  /** DEC-197: finished, with problems it reported (one unreadable file, one lookup that
   *  did not answer). Its findings are kept. Never a failure. */
  | 'done_with_problems'

// ---------------------------------------------------------------------------
// Run time, visibility and failures (DEC-195, DEC-196, DEC-197, SDD §39)
// ---------------------------------------------------------------------------

/** DEC-196: who can see a scan. `private`: only `created_by_user_id`. */
export type ScanVisibility = 'private' | 'workspace'

/** DEC-197: why an agent failed, as recorded. */
export type FailureKind =
  | 'timeout'
  | 'crash'
  | 'ai_service'
  | 'ai_unavailable'
  | 'not_available'
  | 'stopped'
  | 'interrupted'

/** SDD §39.3: the technical detail, behind "Show technical details". */
export interface FailureDetail {
  exception_type: string | null
  exception_message: string | null
  /** The last frames of the stack trace, redacted server-side. A string or its lines. */
  trace_tail: string | string[] | null
}

/** One entry of `scans.agent_failures` (SDD §39.3). `detail` is absent on live events. */
export interface AgentFailure {
  agent_id: string
  kind: FailureKind
  step: string | null
  message: string | null
  kept?: { findings?: number | null; warnings?: number | null; tokens?: number | null } | null
  detail?: FailureDetail | null
}

/** SDD §39.1: one wave's measured run time. */
export interface WaveTiming {
  wave: number
  started_at: string | null
  finished_at: string | null
  run_seconds: number | null
}

export interface AgentTiming {
  agent_id: string
  wave: number | null
  started_at: string | null
  finished_at: string | null
  run_seconds: number | null
  tokens_used: number | null
}

export interface StepTiming {
  started_at: string | null
  finished_at: string | null
  run_seconds: number | null
  tokens_used: number | null
}

/** `scans.timing` (SDD §39.1). Null on scans before migration 036. */
export interface ScanTiming {
  waves?: WaveTiming[]
  agents?: AgentTiming[]
  planner?: StepTiming | null
  critic?: StepTiming | null
  run_time_is_partial?: boolean
}

// ---------------------------------------------------------------------------
// Scan endpoints
// ---------------------------------------------------------------------------

/** DEC-180, SDD §17.12: what an uploaded folder contained, recorded on the scan. */
export interface UploadSourceManifest {
  kind: 'upload'
  folder_name: string
  file_count: number
  bytes: number
  include_git: boolean
  has_git_history: boolean
  git_history_sanitized: boolean
  excluded: { path: string; file_count: number }[]
}

/** GET /api/v1/scans/sources: which ways of starting a scan work on this server. */
export interface ScanSourcesResponse {
  local_path_enabled: boolean
  upload: {
    max_upload_mb: number
    max_files: number
    max_unpacked_mb: number
    excluded_dirs: string[]
  }
}

export interface PostScanRequest {
  repo_url: string
  branch: string
  triggered_by?: string
  github_token?: string
  use_llm?: boolean
  token_budget?: number | null
  /** DEC-196: private (only the person starting it) unless set to `workspace`. */
  visibility?: ScanVisibility
  /** FOR TESTING ONLY — runs waves 1+2 sequentially to avoid rate limits (DEC-032). */
  sequential_waves?: boolean
  /** DEC-094: off by default — the round-cap ceiling behavior is unchanged unless set. */
  enable_round_extension?: boolean
  /** Tier 1, 1–20, no default — automatic same-conversation extension. */
  auto_extension_rounds?: number | null
  /** Tier 2, 1–20, no default, separate from Tier 1 — human-approved extension. */
  approved_extension_rounds?: number | null
}

export interface ScanCreatedResponse {
  /** Null when `state` is 'deferred' (DEC-123): no scan row exists yet, so there is nothing
   *  to navigate to. Callers must branch on `state` rather than assuming an id. */
  scan_id: string | null
  state: 'queued' | 'deferred' | string
  /** Present only when deferred. */
  request_id?: string | null
  /** Why it was deferred, naming which limit applied (DEC-124). */
  reason?: string | null
  /** Scans already in flight that caused the deferral. An in-flight count, not a queue position. */
  deferred_behind?: number | null
  /** Earliest this can start (DEC-126). Usually now, but a request folding into an existing
   *  minimum-interval deferral inherits that earlier boundary, so the message must not
   *  promise "as soon as capacity frees up" when it is really waiting on a clock. */
  run_after?: string | null
}

export interface ScanStatusResponse {
  scan_id: string
  tenant_id: string
  state: ScanState
  repo_url: string
  branch: string
  triggered_by: string
  started_at: string
  completed_at: string | null
  agents_run: string[]
  agents_failed: string[]
  errors: string[]
  /** DEC-171 / SDD 17.9. WHY the scan failed. `errors` is the accumulated agent log and
   *  the terminal failure is appended after it, so errors[0] is an agent error whenever
   *  any agent ran. Never read the cause out of that list. */
  failure_code: string | null
  /** Whether running the same scan again could plausibly succeed. Classified server-side
   *  and defaulting to false: "we do not know" must never render as "try again". */
  failure_is_retryable: boolean
  /** What to change so the next attempt can work. Null when retrying IS the remedy, and
   *  null when there is nothing useful to say -- never filler. */
  failure_remedy: string | null
  /** The sentence describing the failure, resolved server-side. Never index `errors`
   *  here: the cause is appended after the agents' errors, so a client picking a
   *  position would re-derive the bug DEC-171 fixed. */
  failure_message: string | null
  /** DEC-180. What an uploaded folder contained and what was left out of it. Null for
   *  clone and local:// scans. */
  source_manifest?: UploadSourceManifest | null
  total_effort_hours: number
  finding_count: number
  use_llm: boolean
  sequential_waves: boolean
  total_tokens_used: number
  estimated_llm_cost: number
  token_budget: number | null
  /** DEC-067: LLM availability as a first-class scan mode. */
  llm_mode: LlmMode
  llm_degraded_reason: string | null
  llm_degraded_from_agent: string | null
  /** DEC-094: whether this scan can show the per-agent "awaiting decision" affordance at all. */
  enable_round_extension: boolean
  auto_extension_rounds: number | null
  approved_extension_rounds: number | null
  /** DEC-191. {severity: count} of the saved findings, counted in SQL. Empty while a scan
   *  runs; live counts come from GET /api/v1/scans/live. Optional for older mocks. */
  findings_by_severity?: Partial<Record<Severity, number>>
  /** DEC-192. Set while archived (out of the default lists). A deleted record is archived. */
  archived_at?: string | null
  /** DEC-192. Set once deleted permanently: findings erased, this record kept. */
  deleted_at?: string | null
  deleted_by?: string | null
  /** Who deleted it, in words ("Maru Rivero", "an administrator"). */
  deleted_by_name?: string | null
  /** How many findings were erased. `finding_count` is 0 after a deletion. */
  deleted_findings_count?: number | null
  /** DEC-192. For a paused scan: when Archive and Delete permanently are offered. */
  archive_available_at?: string | null
  /** DEC-195. Run time in seconds with every wait for a person taken out. Null on scans
   *  before migration 036: render "run time not recorded", never completed - started. */
  run_seconds?: number | null
  /** DEC-195. True for a scan interrupted by a restart: render "at least". */
  run_time_is_partial?: boolean | null
  /** DEC-195. The scan is waiting for a person (paused, an AI decision, a round decision). */
  waiting_for_person?: boolean | null
  queued_at?: string | null
  run_started_at?: string | null
  timing?: ScanTiming | null
  /** DEC-196. Absent from an older server: treated as `workspace`. */
  visibility?: ScanVisibility | null
  created_by_user_id?: string | null
  /** DEC-196. The creator's name. Null for GitHub and ownerless schedules. */
  created_by_name?: string | null
  /** DEC-196. The caller started this scan, so may change who can see it. */
  owned_by_viewer?: boolean
  /** DEC-197. The scan recorded failure details (after migration 036). */
  recorded_details?: boolean
  visibility_changed_by?: string | null
  visibility_changed_at?: string | null
  /** DEC-196. A GitHub push scan: the pusher's login, a label only. */
  pushed_by?: string | null
  /** DEC-197. Agents that finished with problems (not failures). */
  agents_with_problems?: string[]
  agent_failures?: AgentFailure[] | null
}

/** DEC-067 scan modes — how much AI was involved in this scan.
 * `not_configured` (DEC-087): the tenant has no Anthropic API key at all —
 * distinct from `static_by_choice` (the user opted out for this one scan). */
export type LlmMode =
  | 'full'
  | 'static_by_choice'
  | 'awaiting_decision'
  | 'degraded_credits'
  | 'degraded_auth'
  | 'not_configured'

/** Per-user profile (DEC-084) — hard-gates which report lens a user may open (§30.3). */
export type Profile = 'engineering' | 'executive' | 'product_gtm' | 'admin'

/** The Scans dashboard's four numbers (DEC-191), over every scan, unfiltered. */
export interface ScanStatusCounts {
  in_progress: number
  waiting: number
  needs_a_look: number
  finished: number
}

export type ScanStatusGroup = keyof ScanStatusCounts

export interface ScansListResponse {
  scans: ScanStatusResponse[]
  /** How many match the filters, across every page. */
  total: number
  /** DEC-191: how many the tenant has with no filter. Optional for older mocks. */
  total_all?: number
  status_counts?: ScanStatusCounts
  /** DEC-192: archived scans, deleted records included, for the "Archived (N)" tab. */
  archived_count?: number
}

/** GET /api/v1/scans query (DEC-191). */
export interface ScanListParams {
  limit?: number
  offset?: number
  state?: string
  status?: ScanStatusGroup
  repo?: string
  source?: 'upload' | 'github' | 'url'
  date_from?: string
  date_to?: string
  q?: string
  /** DEC-192. Default on the server: exclude. */
  archived?: 'only' | 'exclude' | 'include'
  /** DEC-196: Mine (scans I started) or Shared (workspace scans). Omitted: all I can see. */
  visibility?: 'mine' | 'shared'
}

/** One queued, running or paused scan from GET /api/v1/scans/live (DEC-191). */
export interface LiveScanSummary {
  scan_id: string
  state: ScanState
  repo_url: string
  branch: string
  started_at: string
  use_llm: boolean
  /** False when the API holds no events for it (queued, or the API restarted). The
   *  progress fields are then unknown, never zero. */
  has_events: boolean
  wave: number | null
  waves_total: number
  agents_done: number
  agents_total: number
  current_agent: string | null
  /** Before the Critic's review. */
  findings_so_far: number
  findings_by_severity: Partial<Record<Severity, number>>
  /** DEC-192. For a paused scan: when Archive and Delete permanently are offered. */
  archive_available_at?: string | null
  /** DEC-195. Computed on the fly by the server; null when not recorded. */
  run_seconds?: number | null
  run_time_is_partial?: boolean | null
  waiting_for_person?: boolean | null
  visibility?: ScanVisibility | null
  created_by_user_id?: string | null
  owned_by_viewer?: boolean
  pushed_by?: string | null
}

export interface LiveScansResponse {
  scans: LiveScanSummary[]
  running: number
  paused: number
  queued: number
  pending: number
  /** DEC-196: slots held by other members' private scans the viewer cannot see. */
  hidden_private_in_flight?: number
}

/** A deferred scan request (DEC-115, DEC-123), GET /api/v1/scans/pending. */
export interface PendingScan {
  request_id: string
  repo_url: string
  branch: string
  run_after: string
  requested_at: string
  triggered_by: string
  trigger_count: number
  use_llm: boolean
  /** The scheduler is starting it; it can no longer be cancelled. */
  starting: boolean
  visibility?: ScanVisibility | null
  created_by_user_id?: string | null
}

export interface PendingScansListResponse {
  requests: PendingScan[]
  total: number
}

// ---------------------------------------------------------------------------
// Findings
// ---------------------------------------------------------------------------

export interface CodeSnippet {
  /** Raw source lines, newline-separated. */
  text: string
  /** 1-indexed line number of the first line in `text`. */
  snippet_start_line: number
}

export type FeedbackVerdict = 'confirmed' | 'false_positive' | 'wont_fix'

export interface FindingResponse {
  finding_id: string
  category: string
  subcategory: string
  file: string
  line_start: number | null
  line_end: number | null
  severity: Severity
  confidence: number
  human_summary: string
  suggested_direction: string | null
  why_it_costs: string | null
  estimated_effort_hours: number
  group_id: string | null
  occurrence_count: number
  /** Source lines captured at scan time. Null for findings from older scans. */
  code_snippet: CodeSnippet | null
  /** Confidence delta applied from prior scan feedback (e.g. -0.15). Null if unaffected. */
  confidence_adjustment: number | null
  /** Human-readable explanation of the confidence adjustment. Null if unaffected. */
  confidence_adjustment_reason: string | null
  /** Current user verdict for this finding. Null = not yet reviewed. */
  feedback_verdict: FeedbackVerdict | null
  /** DEC-067 / DEC-145: the detector that found this. Never overwritten. */
  detection_method: string | null
  /** DEC-145: the LLM step that confirmed or enriched it, or null if none did. */
  confirmed_by: string | null
}

export interface FindingsListResponse {
  findings: FindingResponse[]
  total: number
}

// ---------------------------------------------------------------------------
// Report
// ---------------------------------------------------------------------------

export interface ScanSummary {
  total_findings: number
  total_effort_hours: number
  total_dollar_cost: number
  findings_by_category: Record<string, number>
  findings_by_severity: Record<string, number>
}

/**
 * Severity-weighted MEAN risk per finding. Retained for its `by_category` composition
 * view, no longer a headline (DEC-110): `total` saturates at 100 on a single critical
 * finding, rises when findings are fixed, and falls when debt is added. Use
 * `DebtIndexResponse` for anything compared over time.
 */
export interface RiskScoreResponse {
  total: number
  by_category: Record<string, number>
  methodology: string
}

/**
 * Volume-aware debt metric indexed to the repository's baseline scan (DEC-107).
 * A total rather than a mean, so it rises with added debt and falls with fixed debt.
 * `debt_index` 100 is the baseline; 112 is 12 percent more debt density than baseline.
 *
 * `debt_density` must always be rendered alongside the index: the index alone hides
 * whether the repository started clean or filthy (SDD §34.3).
 */
export interface DebtIndexResponse {
  debt_index: number | null
  debt_density: number
  total_debt_units: number
  total_effort_hours: number
  is_baseline: boolean
  baseline_date: string | null
  denominator_basis: 'kloc' | 'files'
  denominator_value: number
  model_version: string
  comparability: 'full' | 'degraded'
  comparability_reason: string | null
  computed_live: boolean
  disclosure: string
}

/** DEC-146: an agent the Planner did not run, and why. */
export interface SkippedAgent {
  agent_id: string
  reason: string
}

export interface AgentsSummary {
  run: string[]
  failed: string[]
  skipped: SkippedAgent[]
}

/** Files an agent did not read, as a count and a reason. Never the paths: a large repo
 *  skips thousands and D10's cache skips most of an unchanged one. */
export interface SkippedFilesByAgent {
  agent_id: string
  file_count: number
  reason: string
}

/** A language in this repository that no analyzer covers.
 *  Carries its tier (DEC-147): "committed, not yet implemented" and "not supported" are
 *  different answers, and collapsing them tells the reader less than TDIA knows. */
export interface UnanalyzedLanguage {
  language: string
  file_count: number
  tier: LanguageTierName
  tier_meaning: string
}

/** DEC-147: TDIA's language support policy, independent of any scan. */
export type LanguageTierName = 'supported' | 'committed' | 'acknowledged'

export interface LanguageTier {
  tier: LanguageTierName
  languages: string[]
  meaning: string
}

export interface LanguageSupportResponse {
  tiers: LanguageTier[]
}

/**
 * DEC-146: what the scan did not look at.
 *
 * A findings list answers "what is wrong" and never answered "what did you look at", so
 * an empty section read as a clean result. On a repository in a language TDIA cannot
 * analyse, that produced a near-empty report that looked healthy.
 */
/** DEC-151: an agent that ran and examined less than it could.
 *  Distinct from a skipped agent, which never ran at all. Before migration 030 these
 *  existed only as a live SSE field, so a warning explaining that half a repository went
 *  unread was gone the moment the page reloaded. */
export interface PartialAnalysis {
  agent_id: string
  warning: string
}

export interface NotExaminedSummary {
  skipped_agents: SkippedAgent[]
  skipped_files: SkippedFilesByAgent[]
  unanalyzed_languages: UnanalyzedLanguage[]
  partial_analyses: PartialAnalysis[]
  /** Scan predates DEC-146. Render as "not recorded", never as "nothing was skipped". */
  coverage_not_recorded: boolean
  /** What this section covers, and where the per-check view lives. Shown, not hidden. */
  limits: string
}

/** One check's verdict for one scan (DEC-164). */
export interface CheckVerdict {
  check_id: string
  name: string
  category: string
  agent_id: string | null
  /**
   * Five verdicts, not two. `clean` and `not_run` are the pair the product used to render
   * identically and they are opposites: one is assurance, the other is a hole.
   */
  verdict: 'findings' | 'clean' | 'not_applicable' | 'not_run' | 'not_implemented'
  count: number
  /** Only on `clean`, and only where a file count is the honest unit. */
  denominator: number | null
  denominator_unit: string
  /** Why it did not run, or did not apply. Empty on findings and clean. */
  reason: string
  detector: string
  /** Kept for compatibility, always sent empty since DEC-189: an agent's warnings are in
   *  `CoveragePanelSummary.agent_notes`, once per agent, not copied onto each check. */
  notes: string[]
  /** The gap entry behind a `not_implemented` verdict. */
  gap: string | null
}

/** Every check TDIA has, with what it said about this scan (GAP-036 slice 2). */
export interface CoveragePanelSummary {
  checks: CheckVerdict[]
  /** DEC-189: each agent's warnings, once, keyed by agent id. Optional because a
   *  response from a backend that predates it has none, which renders as no warnings. */
  agent_notes?: Record<string, string[]>
  summary: string
  examined: number
  applicable: number
  /** Scan predates the stored facts. Render as "not recorded", never as an empty panel. */
  coverage_not_recorded: boolean
}

export interface TopFinding {
  finding_id: string
  category: string
  /** Always sent by the API (`FindingSummary.subcategory`). Optional here only so older
   *  test fixtures stay valid; the Executive report's plain titles read it (DEC-190). */
  subcategory?: string
  severity: Severity
  confidence: number
  file: string
  line_start: number | null
  human_summary: string
  estimated_effort_hours: number
  dollar_cost: number
}

export interface ScanReportResponse {
  scan_id: string
  state: ScanState
  repo_url: string
  branch: string
  started_at: string
  /** Deprecated by DEC-195: equals `run_seconds` on a new server. */
  duration_seconds: number | null
  run_seconds?: number | null
  run_time_is_partial?: boolean | null
  summary: ScanSummary
  risk_score: RiskScoreResponse
  debt_index: DebtIndexResponse | null
  agents: AgentsSummary
  /** DEC-146: what was not examined, beside what was found. */
  not_examined: NotExaminedSummary
  /** DEC-164: the per-check view, including the checks TDIA does not implement. */
  coverage_panel: CoveragePanelSummary
  top_findings: TopFinding[]
  errors: string[]
  /** DEC-067: LLM availability as a first-class scan mode. */
  llm_mode: LlmMode
  llm_degraded_reason: string | null
  llm_degraded_from_agent: string | null
}

// ---------------------------------------------------------------------------
// SSE events (UI_SDD.md Section 3)
// ---------------------------------------------------------------------------

export interface AgentEvent {
  event_type: 'agent_status_changed'
  scan_id: string
  agent_id: string
  agent_label: string
  status: AgentStatus
  wave: number
  findings_count: number
  tokens_used: number
  duration_seconds: number
  error: string | null    // set when status === 'failed'
  warning: string | null  // set when status === 'warned' (tool not available)
  /** DEC-146: set when status === 'skipped'. Never `error`: a Planner skip is a correct
   *  decision, not a failure, and must not render in the failed state. */
  reason: string | null
  /** DEC-197: on a terminal event of a failed agent (no `detail`). */
  failure?: AgentFailure | null
  /** DEC-197: every warning, not only the first. */
  warnings?: string[] | null
  timestamp: string
}

export interface ScanStateEvent {
  event_type: 'scan_state_changed'
  scan_id: string
  state: ScanState
  /** DEC-197: on `failed`. */
  failure_code?: string | null
  failure_message?: string | null
  timestamp: string
}

/** DEC-195: a wave finished, with its run time. */
export interface WaveFinishedEvent {
  event_type: 'wave_finished'
  scan_id: string
  wave: number
  run_seconds: number | null
  timestamp: string
}

export interface FindingEvent {
  event_type: 'finding_produced'
  scan_id: string
  agent_id: string
  finding_id: string
  category: string
  severity: Severity
  file: string
  human_summary: string
  timestamp: string
}

export interface CriticEvent {
  event_type: 'critic_summary'
  scan_id: string
  total_findings_before: number
  total_findings_after: number
  groups_created: number
  findings_suppressed: number
  timestamp: string
}

export interface BudgetWarningEvent {
  event_type: 'budget_warning'
  scan_id: string
  tokens_used: number
  budget: number
  percent: number
  action: 'warn' | 'skip_llm' | 'abort'
  timestamp: string
}

export interface LlmUnavailableEvent {
  event_type: 'llm_unavailable'
  scan_id: string
  reason: 'credit_exhausted' | 'auth_invalid'
  from_agent: string
  decision: 'awaiting_user' | 'auto_continued'
  timestamp: string
}

/** DEC-094: a specific agent's Tier-2 round-cap pause landed — per-agent,
 * not scan-wide (distinct from LlmUnavailableEvent's scan-wide pause). */
export interface RoundDecisionRequestedEvent {
  event_type: 'round_decision_requested'
  scan_id: string
  agent_id: string
  timestamp: string
}

export interface RoundDecisionResolvedEvent {
  event_type: 'round_decision_resolved'
  scan_id: string
  agent_id: string
  approved: boolean
  granted_rounds: number
  timestamp: string
}

export type ScanEvent =
  | AgentEvent
  | ScanStateEvent
  | FindingEvent
  | CriticEvent
  | BudgetWarningEvent
  | LlmUnavailableEvent
  | RoundDecisionRequestedEvent
  | RoundDecisionResolvedEvent
  | WaveFinishedEvent

// ---------------------------------------------------------------------------
// Agent / category display metadata (UI_SDD.md Section 8)
// ---------------------------------------------------------------------------

// Agent id → backend category code, used for findings-page filters (§R.3).
export const AGENT_CATEGORY_CODE: Record<string, string> = {
  d1_complexity:    'COMPLEXITY',
  d2_dependency:    'SUPPLY_CHAIN',
  d3_testing:       'TEST_COVERAGE',
  d4_architecture:  'ARCHITECTURE',
  d5_security:      'SECURITY',
  d6_documentation: 'DOCUMENTATION',
  d7_performance:   'PERFORMANCE',
  d8_lint:          'CODE_STYLE',
  d9_dead_code:     'DEAD_CODE',
  d10_idiomatic:    'IDIOMATIC',
  d11_design:       'DESIGN',
  d12_coupling:     'COUPLING',
  d13_infrastructure: 'INFRASTRUCTURE',
}

export const AGENT_LABELS: Record<string, string> = {
  d1_complexity: 'Complexity',
  d2_dependency: 'Dependencies',
  d3_testing: 'Test Coverage',
  d4_architecture: 'Architecture',
  d5_security: 'Security',
  d6_documentation: 'Documentation',
  d7_performance: 'Performance',
  d8_lint: 'Lint',
  d9_dead_code: 'Dead Code',
  d11_design: 'Design',
  d12_coupling: 'Coupling',
  d13_infrastructure: 'Infrastructure',
  d10_idiomatic: 'Idiomatic',
  planner: 'Planner',
  critic: 'Critic',
}

/** One-line purpose shown inside every agent card at all states.
 *  Gives non-engineers immediate context before results are ready. */
export const AGENT_DESCRIPTIONS: Record<string, string> = {
  d1_complexity:    'Function size, cyclomatic complexity & duplication',
  d2_dependency:    'CVEs, outdated & unpinned packages',
  d3_testing:       'Missing tests & low assertion density',
  d4_architecture:  'Layer violations & structural coupling',
  d5_security:      'Hardcoded secrets, injection & weak crypto',
  d6_documentation: 'Missing docstrings, README & stale docs',
  d7_performance:   'N+1 queries, blocking I/O & inefficient loops',
  d8_lint:          'Style violations & code quality rules',
  d9_dead_code:     'Orphan modules, uncalled functions & commented code',
  d11_design:       'Swallowed exceptions, empty catch blocks & missing input validation',
  d12_coupling:     'Service mesh, circular deps & coupling blast radius',
  d13_infrastructure: 'Containers, Kubernetes & Terraform defects',
  d10_idiomatic:    'Language idiom violations',
}

export const CATEGORY_LABELS: Record<string, string> = {
  COMPLEXITY:    'Complexity',
  SUPPLY_CHAIN:  'Dependencies',
  TEST_COVERAGE: 'Test Coverage',
  ARCHITECTURE:  'Architecture',
  SECURITY:      'Security',
  DOCUMENTATION: 'Documentation',
  PERFORMANCE:   'Performance',
  CODE_STYLE:    'Lint',
  DEAD_CODE:     'Dead Code',
  COUPLING:      'Coupling',
  IDIOMATIC:     'Idiomatic',
  DESIGN:        'Design',
  INFRASTRUCTURE: 'Infrastructure',
}

export const SUBCATEGORY_LABELS: Record<string, string> = {
  // COMPLEXITY
  cyclomatic_complexity:  'High cyclomatic complexity',
  long_function:          'Function too long',
  large_parameter_list:   'Too many parameters',
  long_file:              'File too long',
  code_duplication:       'Duplicated code',
  generated_file_in_source: 'Generated file in source',
  // SUPPLY_CHAIN
  known_cve:              'Known CVE',
  outdated_dependency:    'Outdated dependency',
  unpinned_dependency:    'Unpinned dependency',
  missing_lockfile:       'No lockfile',
  undeclared_dependency:  'Undeclared dependency',
  unused_dependency:      'Unused dependency',
  license_risk:                  'Licence conflict',
  undeclared_license:            'Undeclared licence',
  missing_license_file:          'No licence file',
  license_declaration_conflict:  'Licence declarations disagree',
  // TEST_COVERAGE
  missing_test_file:      'No test file',
  low_assertion_density:  'Low assertion density',
  missing_edge_case:      'Missing edge-case tests',
  untested_critical_path: 'Untested critical path',
  test_theater:           'Tests that assert nothing',
  // ARCHITECTURE
  layer_violation:        'Layer violation',
  god_module:             'God module',
  coupling_hotspot:       'Coupling hotspot',
  missing_init:           'Missing __init__',
  deep_nesting:           'Deeply nested code',
  flat_overload:          'Too many top-level modules',
  // Read from git history rather than from the source [DEC-165]
  knowledge_concentration: 'Code only one person knows',
  abandoned_complex_code:  'Complex code, author gone',
  hidden_coupling:         'Changes together, never imports',
  // SECURITY
  hardcoded_secret:       'Hardcoded secret',
  sql_injection:          'SQL injection risk',
  path_traversal:         'Path traversal risk',
  weak_crypto:            'Weak cryptography',
  insecure_function:      'Unsafe function call',
  // DOCUMENTATION
  missing_docs:           'Missing documentation',
  stale_docstring:        'Stale docstring',
  self_admitted_debt:     'Self-admitted debt',
  // PERFORMANCE
  n_plus_one_query:       'N+1 query pattern',
  synchronous_io:         'Blocking I/O in async context',
  inefficient_loop:       'Inefficient loop pattern',
  sync_io_in_async:       'Blocking I/O in async context',
  unbounded_load:         'Unbounded data load',
  floating_promise:       'Unawaited promise',
  // CODE_STYLE (ruff/flake8 rules handled by rule label in evidence)
  lint_violation:         'Lint violation',
  // DEAD_CODE
  orphan_module:          'Orphan module',
  uncalled_function:      'Uncalled function',
  commented_out_code:     'Commented-out code',
  unused_export:          'Unused export',
  long_dep_chain:         'Long dependency chain',
  // IDIOMATIC
  idiom_violation:        'Idiom violation',
  // DESIGN — D11 [DEC-053]
  swallowed_exception:               'Swallowed exception',
  empty_catch_block:                 'Empty catch block',
  missing_error_boundary:            'Missing error boundary',
  inconsistent_api_shape:            'Inconsistent API response shape',
  missing_input_validation:          'Missing input validation',
  inconsistent_error_propagation:    'Inconsistent error propagation',
  // DESIGN, accessibility — D11 [DEC-163]
  missing_accessible_name:           'Control with no accessible name',
  missing_alt_text:                  'Image with no alt text',
  click_without_keyboard:            'Click handler with no keyboard access',
  input_without_label:               'Form control with no label',
  positive_tabindex:                 'Positive tabIndex',
  // INFRASTRUCTURE — D13 [DEC-162]
  container_runs_as_root:   'Container runs as root',
  unpinned_base_image:      'Unpinned image tag',
  missing_resource_limits:  'No resource limits',
  missing_health_probe:     'No health probe',
  secret_in_manifest:       'Secret in a manifest',
  privileged_container:     'Privileged container',
  overly_broad_iam:         'Overly broad IAM policy',
  public_ingress:           'Exposed to the public internet',
  unpinned_provider:        'Unpinned Terraform provider',
  // COUPLING — D12 [DEC-050]
  circular_dependency:               'Circular dependency',
  bidirectional_dependency:          'Bidirectional dependency',
  deep_call_chain:                   'Deep call chain',
  mesh_density_cluster:              'Mesh density cluster',
  high_fanout_service:               'High fan-out service',
  chokepoint_service:                'Chokepoint service',
  transitive_coupling_blast_radius:  'Transitive blast radius',
}

export const WAVE_AGENTS: Record<number, string[]> = {
  1: ['d8_lint', 'd2_dependency', 'd6_documentation', 'd13_infrastructure'],
  2: ['d1_complexity', 'd3_testing', 'd5_security', 'd11_design'],
  3: ['d9_dead_code', 'd4_architecture', 'd12_coupling', 'd7_performance'],
  4: ['d10_idiomatic'],
}

export const AGENT_WAVE: Record<string, number> = {
  d8_lint: 1, d2_dependency: 1, d6_documentation: 1, d13_infrastructure: 1,
  d1_complexity: 2, d3_testing: 2, d5_security: 2,
  d9_dead_code: 3, d4_architecture: 3, d12_coupling: 3, d7_performance: 3,
  d11_design: 2,
  d10_idiomatic: 4,
}

// ---------------------------------------------------------------------------
// Dependency graph (Death Star page)
// ---------------------------------------------------------------------------

export interface GraphNode {
  id: string
  type: 'internal' | 'third_party'
  label: string
  layer?: string
  fan_in: number
  fan_out: number
  is_god_module: boolean
  is_orphan: boolean
}

export interface GraphEdge {
  from: string
  to: string
  type: 'import'
}

export interface GraphHotspot {
  node_id: string
  reason: string
  fan_in: number
}

export interface DependencyGraphResponse {
  graph_type: string
  scan_id: string
  nodes: GraphNode[]
  edges: GraphEdge[]
  circular_groups: string[][]
  hotspots: GraphHotspot[]
}

// ---------------------------------------------------------------------------
// Executive Report (SDD §21)
// ---------------------------------------------------------------------------

export interface ExecutiveMetrics {
  scan_id: string
  computed_at: string
  team_size: number
  hourly_rate: number
  total_findings: number
  total_effort_hours: number
  total_debt_cost_dollars: number
  expected_debt_cost_dollars: number  // confidence-weighted rollup (DEC-060)
  velocity_tax_percent: number        // 0.0–0.60 fraction
  annual_velocity_cost_dollars: number
  cost_of_inaction_1yr: number
  cost_of_inaction_3yr: number
  roi_percent: number | null
  break_even_months: number | null
  breach_probability_percent: number  // 0–95
  executive_narrative: string | null
  narrative_generated_at: string | null
  narrative_error: string | null
  /** DEC-190 decision 5: what kind of failure `narrative_error` is. Only `temporary`
   *  is offered "Try again". Optional for a backend that predates it. */
  narrative_error_code?: 'no_key' | 'temporary' | 'other' | null
  methodology_disclosure: string
  benchmark_sources: string[]
  team_size_disclosure: string
}

// ---------------------------------------------------------------------------
// Product / GTM Report (SDD §22)
// ---------------------------------------------------------------------------

export interface SalesBlocker {
  finding_id: string
  human_summary: string
  deal_risk_label: string
  severity: Severity
  estimated_effort_hours: number
}

export interface ProductMetrics {
  scan_id: string
  computed_at: string
  feature_delay_weeks_per_quarter: number
  competitive_velocity_gap_percent: number
  features_at_risk_count: number
  sales_blockers: SalesBlocker[]
  enterprise_risk_label: 'low' | 'medium' | 'high' | 'critical'
  churn_risk_score: number           // 0–100
  reliability_findings_count: number
  gtm_narrative: string | null
  narrative_generated_at: string | null
  narrative_error: string | null
  methodology_disclosure: string
}

// ---------------------------------------------------------------------------
// Settings (DEC-084, DEC-086) — profile=admin only
// ---------------------------------------------------------------------------

export interface SettingsResponse {
  anthropic_key_configured: boolean
  anthropic_key_masked: string | null
  // DEC-129: set only when a key IS configured but no preview can be produced (a row predating
  // migration 025). `anthropic_key_configured` stays true, so this must not render as the
  // empty state: it would invite replacing a key that works.
  anthropic_key_preview_notice: string | null
  github_connected: boolean
  /** DEC-192: days a paused scan waits before Archive and Delete permanently are offered.
   *  1 to 3650, default 90. Optional for older mocks. */
  archive_paused_after_days?: number
  /** DEC-204: scans started today and the daily limit (null = no limit). Read-only. */
  scans_today?: number
  daily_scan_limit?: number | null
  /** DEC-203. */
  plan?: string
  /** DEC-200: after a Marketplace cancellation, the day this workspace's data is deleted. */
  data_deletion_due_at?: string | null
}

export interface GitHubRepoSummary {
  full_name: string
  private: boolean
  html_url: string
  default_branch: string
}

/** DEC-176: what a public repository says about itself, before anyone commits to
 *  scanning it. Every field is optional: a lookup failure must never block a scan, so
 *  the form degrades to a free-text branch field. */
export interface RepoMetaResponse {
  /** null means "we could not ask", never "it has no default". */
  default_branch: string | null
  branches: string[]
  /** The list is capped, so the UI says it is partial rather than implying a branch is
   *  absent because it is not shown. */
  truncated: boolean
  unavailable_reason: string | null
}

export interface GitHubReposResponse {
  repos: GitHubRepoSummary[]
  /** DEC-170: which account these came from. null when it could not be read; the list
   *  still renders. Without it a user cannot tell they connected the wrong account, and
   *  reconnecting silently reuses the same one. */
  connected_login: string | null
}

// ---------------------------------------------------------------------------
// Trend dashboard (SDD §35, DEC-116 to DEC-120)
// ---------------------------------------------------------------------------

/** Which rate produced a point's dollar figure. `recomputed_at_current_rate` is what a
 *  workspace point carries: §35's dollar series is recomputed from effort at the tenant's
 *  current rate, so no single scan's basis applies to it. */
export type RateBasis =
  | 'fixed_150'
  | 'workspace_rate'
  | 'recomputed_at_current_rate'
  | 'unknown'

export type SplitMarkerKind =
  | 'model_version_changed'
  | 'comparability_degraded'
  | 'rate_basis_changed'
  | 'repository_set_changed'

/** One period of one series. A gap is a point too, with every figure null, which is what
 *  keeps the period on the axis instead of compressing the silence away (DEC-119). */
export interface TrendPoint {
  period_start: string
  period_label: string
  debt_index: number | null
  debt_density: number | null
  total_debt_units: number | null
  total_effort_hours: number | null
  debt_cost_dollars: number | null
  scan_count: number
  repositories: number | null
  scan_id: string | null
  is_gap: boolean
  is_incomplete: boolean
  comparability: string | null
  comparability_reason: string | null
  model_version: string | null
  denominator_basis: string | null
  rate_basis: RateBasis
  note: string | null
  /** DEC-184: movement against the previous measured point. Optional so the page degrades
   *  to "movement not available" against a server that does not send them yet. A point with
   *  nothing to compare against carries `gap_available: false` and a reason, never zeros. */
  open_findings?: number | null
  new_findings?: number | null
  fixed_findings?: number | null
  not_re_examined?: number | null
  gap_available?: boolean
  gap_unavailable_reason?: string | null
}

/** DEC-184 decision 8: a finding worth opening first, from a repository's latest measured
 *  scan. `kind` is `new` unless nothing was new, then the highest-severity `still_open`. */
export interface TrendLookFirst {
  fingerprint: string
  fingerprint_tier: number | null
  category: string
  subcategory: string
  file: string
  /** The scan's own severity string; normally a `Severity`, rendered defensively. */
  severity: string
  human_summary: string
  estimated_effort_hours: number
  not_examined_reason: string | null
  repo_url: string
  kind: 'new' | 'still_open'
}

export interface TrendProjectionMonth {
  period_start: string
  period_label: string
  /** Null leaves a gap in the dashed line, as a measured gap does. */
  debt_index: number | null
}

/** DEC-184 decision 9: the "if nothing is fixed" scenario to December, month period only.
 *  Always an object from the server; `months` is empty and `blocked_reason` says why when
 *  it cannot be computed (and for any period other than month). */
export interface TrendProjection {
  method: string
  months: TrendProjectionMonth[]
  blocked_reason: string | null
}

export interface TrendSplitMarker {
  period_start: string
  kind: SplitMarkerKind
  detail: string
}

export interface TrendRepoSeries {
  repo_url: string
  branch: string
  points: TrendPoint[]
  split_markers: TrendSplitMarker[]
  notes: string[]
}

export interface TrendComparison {
  period_start: string
  period_label: string
  debt_index: number | null
  prior_period_label: string | null
  prior_debt_index: number | null
  delta: number | null
  year_ago_debt_index: number | null
  year_over_year_delta: number | null
  blocked_reason: string | null
}

/** DEC-118. Three states, and all three name what is missing. */
export interface KtloCard {
  actual_share: number | null
  target_share: number | null
  variance: number | null
  missing_inputs: string[]
  blocked_reason: string | null
  caveats: string[]
}

/** The measured Layer 1 companion. Never labelled KTLO on any surface (DEC-118). */
export interface ChurnShare {
  share_percent: number | null
  debt_carrying_files: number
  churn_lines_in_debt_files: number
  churn_lines_measured: number
  churn_lines_excluded: number
  history_available: boolean
  window_start_iso: string | null
  window_scoped: boolean
  disclosure: string
  notes: string[]
}

export interface TrendGapSummary {
  available: boolean
  unavailable_reason: string | null
  scan_id: string | null
  prior_scan_id: string | null
  new: number
  persisting: number
  resolved: number
  not_re_examined: number
  not_examined_reasons: string[]
  effort_hours_delta: number
  comparable: boolean
  comparability_blockers: string[]
  methodology_disclosure: string | null
}

export interface ExcludedRepo {
  repo_url: string
  branch: string
  denominator_basis: string
  reason: string
}

export type TrendPeriod = 'month' | 'quarter' | 'week'

export interface TrendsResponse {
  tenant_id: string
  period_type: TrendPeriod
  period_note: string | null
  has_trend: boolean
  workspace: TrendPoint[]
  workspace_split_markers: TrendSplitMarker[]
  repos: TrendRepoSeries[]
  comparison: TrendComparison | null
  weighting_statement: string
  excluded_repos: ExcludedRepo[]
  hourly_rate: number
  cost_basis_disclosure: string
  ktlo: KtloCard
  fte_equivalent: number | null
  fte_blocked_reason: string | null
  churn_share: ChurnShare | null
  gap: TrendGapSummary
  notes: string[]
  /** DEC-196: which scans the trend is built from. Optional for an older server. */
  scope?: TrendScope | null
  /** DEC-192: points of scans deleted permanently, left out of the series. The sentence
   *  that names them is in `notes`. Optional for older servers. */
  deleted_scans_excluded?: number
  /** DEC-184. Optional so an older server still renders the page. */
  look_first?: TrendLookFirst[]
  projection?: TrendProjection | null
}

/** DEC-196: the viewer's own scans, plus the team repositories they chose to include. */
export interface TrendScope {
  own_scan_count: number
  team_scans_available: { repo: string; scan_count: number }[]
  team_repos_included: string[]
}

export interface TrendNarrativeResponse {
  tenant_id: string
  period_type: TrendPeriod
  period_start: string
  period_label: string
  narrative: string | null
  error: string | null
  attribution: string
  cached: boolean
}

export interface ImpactParameter {
  key: string
  label: string
  value: number | null
  unit: string
  provenance: string
  source: string | null
  calibration_status: string
  caveat: string
  settable: boolean
  derived_from: string[] | null
}

export interface BlockedOutput {
  metric: string
  missing_parameters: string[]
  reason: string
}

export interface ImpactInputsResponse {
  tenant_id: string
  parameters: ImpactParameter[]
  missing_customer_inputs: string[]
  blocked_outputs: BlockedOutput[]
  assumption_register: Record<string, unknown>[]
  benchmark_sources: string[]
  method_standard: string
}

export const TREND_PERIOD_LABELS: Record<TrendPeriod, string> = {
  month: 'Monthly',
  quarter: 'Quarterly',
  week: 'Weekly',
}

// ---------------------------------------------------------------------------
// What happened (DEC-197, SDD §39.3): GET /api/v1/scans/{id}/what-happened
// ---------------------------------------------------------------------------

/** Mirrors tdia.api.schemas.TimelineEntry. */
export interface WhatHappenedStep {
  /** queued | started | wave | planner | critic | finished | failed | interrupted */
  kind: string
  label: string
  at: string | null
  run_seconds?: number | null
  /** ok | problems | failed | stopped | skipped */
  status?: string | null
  detail?: string | null
}

/** Mirrors tdia.api.schemas.AgentProblemsOut. */
export interface WhatHappenedAgent {
  agent_id: string
  label: string
  /** failed | done_with_problems | warned | complete | skipped | not_run */
  status: string
  errors: string[]
  problems: string[]
  failure: AgentFailure | null
}

/** Mirrors tdia.api.schemas.WhatHappenedResponse. */
export interface WhatHappenedResponse {
  scan_id: string
  state: string
  summary: string
  timeline: WhatHappenedStep[]
  /** Every recorded problem, grouped by agent. */
  agents: WhatHappenedAgent[]
  /** `errors` lines that belong to no agent (the scan's own failure, the planner). */
  scan_errors: string[]
  failure_code: string | null
  failure_message: string | null
  failure_remedy: string | null
  failure_is_retryable?: boolean
  run_seconds: number | null
  run_time_is_partial: boolean
  /** False for scans before migration 036: details were not recorded. */
  recorded_details: boolean
}

// ---------------------------------------------------------------------------
// Usage (SDD §36, by member since DEC-196): GET /api/v1/usage, admins only
// ---------------------------------------------------------------------------

export interface UsageBucket {
  key: string
  scans: number
  tokens: number
  estimated_cost: number
  suppressed_triggers: number
}

/** One member's scans and spend. `user_id` null: GitHub pushes or ownerless schedules. */
export interface UsageMember {
  user_id: string | null
  name: string | null
  scan_count: number
  private_scan_count: number
  spend: number
}

export interface UsageResponse {
  tenant_id: string
  total_scans: number
  total_tokens: number
  total_estimated_cost: number
  current_month: string
  current_month_scans: number
  by_month: UsageBucket[]
  by_repo: UsageBucket[]
  by_trigger: UsageBucket[]
  /** DEC-196. Optional for an older server. */
  by_member?: UsageMember[]
  /** DEC-199: the same rows for `current_month` only, matching the month the chart shows. */
  by_member_current_month?: UsageMember[]
  unattributed_cost?: number
  unattributed_cost_current_month?: number
  unattributed_reason?: string | null
}
